// src/shared/json-table/utils.ts
import type { Filter, FilterOp, Sort } from "./types";

export const encodeSortParam = (sort: Sort[]): string =>
  sort
    .map((s) => `${s.id}.${s.desc ? "desc" : "asc"}`)
    .join(",");

export const decodeSortParam = (s: string | null): Sort[] => {
  if (!s) return [];
  return s.split(",").filter(Boolean).map((pair) => {
    const [id, dir] = pair.split(".");
    return { id, desc: dir === "desc" };
  });
};

export const encodeFiltersParam = (filters: Filter[]): string =>
  filters
    .map((f) => {
      const v = Array.isArray(f.value)
        ? `(${f.value.map(escapeValue).join(",")})`
        : escapeValue(f.value);
      return `${f.id}.${f.op}.${v}`;
    })
    .join(";");

export const decodeFiltersParam = (s: string | null): Filter[] => {
  if (!s) return [];
  return s.split(";").filter(Boolean).map((e) => {
    // id.op.value  OR id.in.(a,b)
    const firstDot = e.indexOf(".");
    const secondDot = e.indexOf(".", firstDot + 1);
    if (firstDot === -1 || secondDot === -1) return null as any;
    const id = e.slice(0, firstDot);
    const op = e.slice(firstDot + 1, secondDot) as FilterOp;
    const raw = e.slice(secondDot + 1);
    const value =
      raw.startsWith("(") && raw.endsWith(")")
        ? raw.slice(1, -1).split(",").map(unescapeValue)
        : unescapeValue(raw);
    return { id, op, value } as Filter;
  }).filter(Boolean) as Filter[];
};

const escapeValue = (v: unknown): string => {
  if (v === null) return "null";
  if (typeof v === "boolean") return v ? "true" : "false";
  if (typeof v === "string" && (v.includes(",") || v.includes(" "))) {
    return `"${v.replace(/"/g, '\\"')}"`;
  }
  return String(v);
};
const unescapeValue = (s: string): string => {
  if (s === "null") return null as unknown as string;
  if (s === "true") return "true";
  if (s === "false") return "false";
  if (s.startsWith('"') && s.endsWith('"')) {
    return s.slice(1, -1).replace(/\\"/g, '"');
  }
  return s;
};

export const shallowEqual = (a: unknown, b: unknown) => {
  try { return JSON.stringify(a) === JSON.stringify(b); } catch { return a === b; }
};

export const clamp = (n: number, min: number, max: number) =>
  Math.min(max, Math.max(min, n));

export const defaultOpForType = (type?: string): FilterOp =>
  type === "text" || type === undefined ? "ilike" :
  type === "boolean" ? "eq" :
  type === "select" ? "eq" :
  /* number | date */ "eq";

export const nextSort = (current: Sort | undefined): Sort | undefined => {
  if (!current) return { id: "", desc: false } as Sort;
  if (current.desc === false) return { ...current, desc: true };
  return undefined;
};